import { Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  showHome?: boolean;
  separator?: 'chevron' | 'slash';
  className?: string;
}

export function Breadcrumbs({
  items,
  showHome = true,
  separator = 'chevron',
  className,
}: BreadcrumbsProps) {
  const allItems = showHome
    ? [{ label: 'Home', href: '/', icon: HomeIcon }, ...items]
    : items;

  const SeparatorIcon =
    separator === 'chevron' ? (
      <ChevronRightIcon className="h-4 w-4 text-gray-400 dark:text-dark-500 flex-shrink-0" />
    ) : (
      <span className="text-gray-400 dark:text-dark-500">/</span>
    );

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center', className)}>
      <ol className="flex items-center flex-wrap gap-1">
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          const Icon = item.icon;

          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <span className="mx-2 flex-shrink-0">{SeparatorIcon}</span>
              )}

              {isLast ? (
                <span
                  className={cn(
                    'text-sm font-medium text-gray-900 dark:text-white',
                    'flex items-center gap-1.5'
                  )}
                  aria-current="page"
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span className="line-clamp-1">{item.label}</span>
                </span>
              ) : item.href ? (
                <Link
                  to={item.href}
                  className={cn(
                    'text-sm text-gray-500 dark:text-gray-400',
                    'hover:text-primary-600 dark:hover:text-primary-400',
                    'transition-colors flex items-center gap-1.5'
                  )}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span className="line-clamp-1">{item.label}</span>
                </Link>
              ) : (
                <span
                  className={cn(
                    'text-sm text-gray-500 dark:text-gray-400',
                    'flex items-center gap-1.5'
                  )}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span className="line-clamp-1">{item.label}</span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

interface BreadcrumbsCompactProps {
  items: BreadcrumbItem[];
  maxItems?: number;
  className?: string;
}

export function BreadcrumbsCompact({
  items,
  maxItems = 3,
  className,
}: BreadcrumbsCompactProps) {
  if (items.length <= maxItems) {
    return <Breadcrumbs items={items} showHome={false} className={className} />;
  }

  const first = items[0];
  const last = items.slice(-2);
  const collapsed = items.slice(1, -2);

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center', className)}>
      <ol className="flex items-center flex-wrap gap-1">
        {/* First item */}
        <li className="flex items-center">
          {first.href ? (
            <Link
              to={first.href}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {first.label}
            </Link>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {first.label}
            </span>
          )}
        </li>

        {/* Collapsed indicator */}
        {collapsed.length > 0 && (
          <li className="flex items-center">
            <ChevronRightIcon className="mx-2 h-4 w-4 text-gray-400 dark:text-dark-500" />
            <span
              className="text-sm text-gray-400 dark:text-dark-500 cursor-default"
              title={collapsed.map((i) => i.label).join(' > ')}
            >
              ...
            </span>
          </li>
        )}

        {/* Last items */}
        {last.map((item, index) => {
          const isLast = index === last.length - 1;

          return (
            <li key={index} className="flex items-center">
              <ChevronRightIcon className="mx-2 h-4 w-4 text-gray-400 dark:text-dark-500" />
              {isLast ? (
                <span
                  className="text-sm font-medium text-gray-900 dark:text-white"
                  aria-current="page"
                >
                  {item.label}
                </span>
              ) : item.href ? (
                <Link
                  to={item.href}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
